import { createGlobalStyle } from 'styled-components';
import type { ThemeMode } from '../types';

export type AppTheme = {
  mode: ThemeMode;
  bg: string;
  chrome: string;
  chromeMuted: string;
  surface: string;
  surfaceStrong: string;
  surfaceSoft: string;
  text: string;
  muted: string;
  border: string;
  primary: string;
  primaryStrong: string;
  primarySoft: string;
  danger: string;
  dangerStrong: string;
  dangerSoft: string;
  success: string;
  successSoft: string;
  warning: string;
  assistant: string;
  user: string;
  shadow: string;
  hoverShadow: string;
  codeBg: string;
  codeText: string;
};

export const themes: Record<ThemeMode, AppTheme> = {
  light: {
    mode: 'light',
    bg: '#f7f9fd',
    chrome: '#081122',
    chromeMuted: '#8ea0bd',
    surface: '#ffffff',
    surfaceStrong: '#ffffff',
    surfaceSoft: '#f7f9fd',
    text: '#081122',
    muted: '#52525b',
    border: '#e2e8ee',
    primary: '#007afe',
    primaryStrong: '#006fe5',
    primarySoft: 'rgba(0, 122, 254, 0.08)',
    danger: '#ff3b30',
    dangerStrong: '#d92d20',
    dangerSoft: '#fff0ef',
    success: '#0d957a',
    successSoft: '#e7f6f2',
    warning: '#ff9c00',
    assistant: '#ffffff',
    user: '#eef7ff',
    shadow: '0 1px 2px rgba(8,17,34,0.06), 0 4px 12px rgba(8,17,34,0.04)',
    hoverShadow: '0 4px 16px rgba(0,122,254,0.18)',
    codeBg: '#081122',
    codeText: '#f7f9fd',
  },
  dark: {
    mode: 'dark',
    bg: '#0a0f1c',
    chrome: '#040811',
    chromeMuted: '#a5b4d0',
    surface: '#141c2d',
    surfaceStrong: '#182136',
    surfaceSoft: '#0f1424',
    text: '#f3f5fa',
    muted: '#a5b4d0',
    border: '#27324a',
    primary: '#4ea3ff',
    primaryStrong: '#7ea8ff',
    primarySoft: 'rgba(78, 163, 255, 0.12)',
    danger: '#ff6b62',
    dangerStrong: '#ff9b94',
    dangerSoft: 'rgba(255, 59, 48, 0.14)',
    success: '#5fd4ad',
    successSoft: 'rgba(13, 149, 122, 0.18)',
    warning: '#ffb14a',
    assistant: '#141c2d',
    user: 'rgba(78, 163, 255, 0.12)',
    shadow: '0 1px 2px rgba(0,0,0,0.25), 0 10px 30px rgba(0,0,0,0.20)',
    hoverShadow: '0 4px 18px rgba(78,163,255,0.20)',
    codeBg: '#050914',
    codeText: '#f3f5fa',
  },
};

export const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  html, body, #root { min-height: 100%; }

  body {
    margin: 0;
    min-width: 320px;
    background: ${p => p.theme.bg};
    color: ${p => p.theme.text};
    font-family: 'DM Sans', system-ui, -apple-system, sans-serif;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }

  button, input { font: inherit; }
  button { cursor: pointer; }

  code, pre {
    font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }

  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-thumb { background: #cbd3dd; border-radius: 999px; }
  ::-webkit-scrollbar-thumb:hover { background: ${p => p.theme.muted}; }
  ::-webkit-scrollbar-track { background: transparent; }
`;
